import React from 'react';
import {
  Typography,
  TypographyProps,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  styled,
  Box
} from '@mui/material';
import DatePicker from "react-datepicker";
import { RenewalInfo } from '../../../../types/journalEntry';
import { BasePaper, GlassInput } from './styles/SharedStyles';
import { FiBell, FiCalendar, FiClock, FiAlertCircle } from 'react-icons/fi';

const ReminderPaper = styled(BasePaper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  background: 'rgba(26, 31, 46, 0.9) !important',
  backdropFilter: 'blur(10px)',
  width: '100%',
  borderTop: '2px solid transparent',
  borderImage: 'linear-gradient(90deg, #0EA5E9 0%, #22D3EE 100%) 1',
  color: 'white',
  zIndex: 10,
  opacity: 1,
  position: 'relative',
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9) !important',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    color: '#0EA5E9',
    fontSize: '1.2rem',
  }
}));

const StyledInput = styled(GlassInput)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.03) !important',
    color: 'rgba(255, 255, 255, 0.9) !important',
    '& input, & .MuiSelect-select, & textarea': {
      color: 'rgba(255, 255, 255, 0.9) !important',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7) !important',
    }
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.9) !important',
  },
  '& .MuiMenu-paper': {
    background: 'rgba(26, 31, 46, 0.95) !important',
    backdropFilter: 'blur(10px)',
  },
  '& .MuiMenuItem-root': {
    color: 'rgba(255, 255, 255, 0.9) !important',
    '&:hover': {
      background: 'rgba(14, 165, 233, 0.1) !important',
    },
    '&.Mui-selected': {
      background: 'rgba(14, 165, 233, 0.2) !important',
      '&:hover': {
        background: 'rgba(14, 165, 233, 0.3) !important',
      }
    }
  }
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9) !important',
  '&:hover': {
    background: 'rgba(14, 165, 233, 0.1) !important',
  },
  '&.Mui-selected': {
    background: 'rgba(14, 165, 233, 0.2) !important',
    '&:hover': {
      background: 'rgba(14, 165, 233, 0.3) !important',
    }
  }
}));

interface StyledInfoTextProps extends TypographyProps {
  component?: React.ElementType;
}

const StyledInfoText = styled(Box)(({ theme }) => ({
  color: '#22D3EE !important',
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1rem',
    color: '#0EA5E9 !important',
  }
}));

interface RenewalInfoFormProps {
  renewalInfo: RenewalInfo;
  onRenewalInfoChange: (info: RenewalInfo) => void;
}

export const RenewalInfoForm: React.FC<RenewalInfoFormProps> = ({
  renewalInfo,
  onRenewalInfoChange
}) => {
  return (
    <ReminderPaper>
      <HeaderTypography variant="h6">
        <FiAlertCircle />
        Renewal Information
      </HeaderTypography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StyledInput
            select
            fullWidth
            label="Renewal Type"
            value={renewalInfo.type}
            onChange={(e) => onRenewalInfoChange({
              ...renewalInfo,
              type: e.target.value as RenewalInfo['type']
            })}
          >
            <StyledMenuItem value="contract">Contract</StyledMenuItem>
            <StyledMenuItem value="subscription">Subscription</StyledMenuItem>
            <StyledMenuItem value="filing">Filing</StyledMenuItem>
            <StyledMenuItem value="registration">Registration</StyledMenuItem>
            <StyledMenuItem value="other">Other</StyledMenuItem>
          </StyledInput>
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            selected={renewalInfo.nextRenewalDate}
            onChange={(date: Date) => onRenewalInfoChange({
              ...renewalInfo,
              nextRenewalDate: date
            })}
            customInput={
              <StyledInput
                fullWidth
                label="Next Renewal Date"
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StyledInput
            type="number"
            fullWidth
            label="Notify Days Before"
            value={renewalInfo.notifyBefore}
            onChange={(e) => onRenewalInfoChange({
              ...renewalInfo,
              notifyBefore: parseInt(e.target.value)
            })}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={renewalInfo.autoRenew}
                onChange={(e) => onRenewalInfoChange({
                  ...renewalInfo,
                  autoRenew: e.target.checked
                })}
              />
            }
            label="Auto Renew"
            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
          />
        </Grid>
        {renewalInfo.type !== 'other' && (
          <Grid item xs={12}>
            <StyledInput
              fullWidth
              multiline
              rows={2}
              label="Notes"
              value={renewalInfo.notes || ''}
              onChange={(e) => onRenewalInfoChange({
                ...renewalInfo,
                notes: e.target.value
              })}
            />
          </Grid>
        )}
      </Grid>
      <StyledInfoText>
        <FiClock />
        How often should this payment recur?
      </StyledInfoText>
    </ReminderPaper>
  );
}; 